import { useMemo, useCallback, useState } from 'react';
import { Button, DialogActions, makeStyles, useTheme } from '@material-ui/core';
import { WbCardContent } from '../../../WbCard';
import { WbButton } from '../../../WbButton';
import { useGeneralPurposePickerContext } from '../../GeneralPurposePickerContext';
import {
  GenericEntityType,
  KindEnum,
  Provider,
  TemplateEntity,
  TextFieldValue,
} from '../../types';
import { ProviderFilters } from '../CommonFilter';
import { EntityKindSelector } from '../EntityKindSelector';
import { SelectableEntityTable } from '../SelectableEntityTable';
import { WbNoData } from '../../../WbNoData';
import { selectTemplateEntity } from '../utils';
import {
  WbDockableGroup,
  WbDockablePanel,
  WbDockableResizeHandle,
} from '../../../WbDockable';
import { SelectedEntities } from './SelectedEntities';
import {
  useWittyTable,
  useWittyAssistantEnabled,
} from '@agilelab/plugin-wb-witty-react';
import { WbAlert } from '../../../WbAlert';
import { useLoaderNunjucks } from '../../../../hooks';

const useStyles = makeStyles(
  () => ({
    cardContent: {
      overflow: 'auto',
      width: '100%',
      padding: '12px',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      padding: '8px 0px',
      alignItems: 'center',
    },
  }),
  { name: 'EntityListMultiPicker' },
);

interface MultiSelectionProps {
  providerMapper: Map<string, Provider<GenericEntityType[], ProviderFilters>>;
  selectedEntityProvider?: Provider<GenericEntityType[], ProviderFilters>;
  availableKinds: TemplateEntity[];
  parentTemplateRef?: string;
}

export const MultipleSelectionTable = ({
  selectedEntityProvider,
  entities,
  setEntities,
}: {
  selectedEntityProvider: Provider<GenericEntityType[], ProviderFilters>;
  entities: TextFieldValue[];
  setEntities: React.Dispatch<React.SetStateAction<TextFieldValue[]>>;
}) => {
  const context = selectedEntityProvider?.useContext();
  const wittyTable = useWittyTable();
  const { enabled: wittyEnabled } = useWittyAssistantEnabled();
  const adapter = useCallback(
    (entity: GenericEntityType) => selectedEntityProvider?.adapt(entity),
    [selectedEntityProvider],
  );
  return (
    <SelectableEntityTable
      multiple
      columns={context?.columns ?? []}
      loading={context?.entitiesState.loading ?? false}
      data={(context?.entitiesState.value as GenericEntityType[]) ?? []}
      error={context?.entitiesState.error}
      adapter={adapter}
      entities={entities}
      onChangeHandler={setEntities}
      filtersSection={selectedEntityProvider?.filtersSection!}
      pagination={context?.pagination!}
      setPagination={context?.setPagination!}
      count={context?.count ?? 0}
      nextCursor={context?.nextCursor}
      prevCursor={context?.prevCursor}
      templateEntity={context?.templateKind!}
      isAiMatchAvailable={
        (wittyEnabled &&
          context.templateKind?.type === KindEnum.remote &&
          context.templateKind?.wittySuggestions?.enabled &&
          wittyTable !== undefined) ??
        false
      }
      getAllFilteredEntities={(context as any)?.getAllFilteredEntities}
      treeMode={context?.treeMode}
    />
  );
};

export const MultiSelection: React.FC<MultiSelectionProps> = ({
  availableKinds,
  parentTemplateRef,
  providerMapper,
}) => {
  const theme = useTheme();
  const classes = useStyles();

  const {
    setSelectedValues,
    setOpenDialog,
    selectedKind,
    selectedValues,
    formContext,
    selectedEntityProvider,
    errorMessage,
  } = useGeneralPurposePickerContext();

  const [entities, setEntities] = useState<TextFieldValue[]>(
    (selectedValues?.tableValues as TextFieldValue[]) ?? [],
  );

  const selectedTemplate = useMemo(
    () => selectTemplateEntity(availableKinds, selectedKind),
    [availableKinds, selectedKind],
  );

  // Preload nunjucks template before rendering the table, setEntities needed parsed display field
  const displayField = selectedTemplate?.displayField
    ? [selectedTemplate?.displayField]
    : undefined;
  useLoaderNunjucks(displayField);

  const renderedComponent = useMemo(
    () =>
      selectedEntityProvider?.renderContextProvider(
        selectedTemplate,
        <MultipleSelectionTable
          selectedEntityProvider={selectedEntityProvider}
          entities={entities}
          setEntities={setEntities}
        />,
        parentTemplateRef,
        formContext,
      ),
    [
      selectedEntityProvider,
      selectedTemplate,
      entities,
      parentTemplateRef,
      formContext,
    ],
  );

  return (
    <WbCardContent
      style={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '64vh',
        flex: 1,
        padding: 0,
      }}
    >
      {errorMessage && <WbAlert message={errorMessage} />}
      <WbDockableGroup direction="horizontal">
        <WbDockablePanel
          defaultSize={70}
          title="Search for entities"
          headerActions={
            availableKinds.length > 1 && (
              <EntityKindSelector availableKinds={availableKinds} />
            )
          }
        >
          <WbCardContent className={classes.cardContent}>
            {renderedComponent ?? <WbNoData />}
          </WbCardContent>
        </WbDockablePanel>

        <WbDockableResizeHandle
          showIconOnHover
          divider
          direction="horizontal"
        />

        <WbDockablePanel
          maxSize={30}
          title="Your selection"
          defaultSize={30}
          headerActions={
            <WbButton
              variant="outlined"
              size="small"
              color="primary"
              disabled={!entities?.length}
              onClick={() => setEntities([])}
              label="Clear all"
            />
          }
        >
          <SelectedEntities
            entities={entities ?? []}
            setEntities={setEntities}
            selectableEntities={availableKinds.length}
            providerMapper={providerMapper}
          />
        </WbDockablePanel>
      </WbDockableGroup>

      <DialogActions
        style={{
          height: '48px',
          position: 'sticky',
          bottom: 0,
          borderTop: `1px solid ${theme.palette.grey[300]}`,
          background: theme.palette.white,
          zIndex: 1,
        }}
      >
        <Button
          variant="outlined"
          color="primary"
          size="small"
          onClick={() => {
            setOpenDialog(false);
          }}
        >
          Cancel
        </Button>
        <WbButton
          onClick={() => {
            setSelectedValues(preValue => {
              return {
                tableValues: entities.map(entity => {
                  const { isAiGenerated, ...rest } = entity;
                  return rest;
                }),
                arbitraryValues: preValue?.arbitraryValues ?? [],
              };
            });
            setOpenDialog(false);
          }}
          variant="contained"
          size="small"
          color="primary"
          label="Apply"
        />
      </DialogActions>
    </WbCardContent>
  );
};
